import type { ChatMessageFeedItem, FeedCardProps, ObjectListFeedItem, ObjectDetailFeedItem } from "./feedTypes.js";
import { useMemo, useState, useEffect, useRef } from "react";
import { getAuthHeaders } from "../../api/auth.js";
import "./Card.css";
import { formatTableValue, getUserLocale } from "./tableValueFormatter.js";
import { WorkflowRenderer } from "../../workflow/WorkflowRenderer.js";
import type { WorkflowUIConfig } from "../../workflow/types.js";
import type { PathContext } from "../../workflow/utils.js";
import { EditableTitle } from "./components/EditableTitle.js";
import { ObjectListCard } from "./ObjectListCard.js";
import { ObjectDetailCard } from "./ObjectDetailCard.js";
import { CameraUpload } from "./CameraUpload.js";

export function Card({ item, onAction, isFullscreen: isFullscreenProp, chatHistory = [] }: FeedCardProps<ChatMessageFeedItem>) {
  const config = item.config || {};
  const { question = '', answer = '', tableData, timestamp, displayMode, kpiData, workflowUI } = config;

  // Debug logging
  console.log('[ChatMessageCard] Card rendered:', {
    itemId: item.id,
    hasConfig: !!config,
    hasWorkflowUI: !!workflowUI,
    workflowUIType: typeof workflowUI,
    configKeys: Object.keys(config),
    hasMessagePayload: !!config?.messagePayload,
    messagePayload: config?.messagePayload
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(tableData?.pagination?.pageSize || 10);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [isEditingQuestion, setIsEditingQuestion] = useState((item as any).isEditing || false);
  const [editedQuestion, setEditedQuestion] = useState(question || '');
  const questionInputRef = useRef<HTMLTextAreaElement>(null);
  const [editedRowData, setEditedRowData] = useState<Record<string, any>>({});
  const [isEditingField, setIsEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showCardMenu, setShowCardMenu] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [questionSuggestions, setQuestionSuggestions] = useState<string[]>([]);

  // Use external fullscreen state from parent
  const isFullscreen = isFullscreenProp ?? false;

  // Sync editedQuestion with question from config
  useEffect(() => {
    setEditedQuestion(question || '');
  }, [question]);

  // Filter chat history for suggestions when editing new card
  useEffect(() => {
    const isNewCard = (item as any).isEditing;
    console.log('[ChatMessageCard] useEffect - isNewCard:', isNewCard, 'editedQuestion:', editedQuestion, 'chatHistory length:', chatHistory.length);

    if (isNewCard && editedQuestion.trim().length > 0) {
      const searchTerm = editedQuestion.trim().toLowerCase();
      const filtered = chatHistory
        .filter(q => q.toLowerCase().includes(searchTerm))
        .filter(q => q.toLowerCase() !== searchTerm)
        .slice(0, 5); // Max 5 suggestions

      console.log('[ChatMessageCard] Filtered suggestions:', filtered);
      setQuestionSuggestions(filtered);
    } else {
      setQuestionSuggestions([]);
    }
  }, [editedQuestion, chatHistory, item]);

  // Auto-focus question input for new cards
  useEffect(() => {
    if ((item as any).isEditing && questionInputRef.current) {
      questionInputRef.current.focus();
    }
  }, [(item as any).isEditing]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showCardMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowCardMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCardMenu]);

  // Initialize workflow context with recordData if available
  const [workflowContext, setWorkflowContext] = useState<PathContext>(() => {
    // Check if we have recordData in the message payload
    const msgPayload = config?.messagePayload as any;
    const recordData = msgPayload?.recordData && typeof msgPayload.recordData === 'object'
      ? msgPayload.recordData
      : {};

    // Clean up recordData: remove internal MongoDB fields
    const cleanedData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(recordData)) {
      // Skip internal fields that shouldn't be in the form
      if (key === '_id' || key === 'type' || key === 'tenantId' || key === 'createdAt' || key === 'updatedAt') {
        continue;
      }

      // Convert ISO date strings to datetime-local format for HTML inputs
      if (typeof value === 'string' && key.includes('At') && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        // ISO: "2025-11-26T10:51:00.000Z"
        // datetime-local: "2025-11-26T10:51"
        cleanedData[key] = value.substring(0, 16); // Keep only YYYY-MM-DDTHH:mm
      } else {
        cleanedData[key] = value;
      }
    }

    return {
      session: cleanedData,
      workflow: null,
      uiAction: null,
      extras: null
    };
  });

  const userLocale = useMemo(() => getUserLocale(), []);

  const handleClick = () => {
    if (onAction && !isLoading) {
      onAction({ id: `drillDown-${item.id}`, type: "command", target: "drillDown", paramKey: item.id });
    }
  };

  const handleQuestionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingQuestion(true);
  };

  const handleQuestionKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && editedQuestion.trim()) {
      e.preventDefault();
      setIsEditingQuestion(false);
      // Trigger refresh with new question
      if (onAction) {
        onAction({
          id: `refresh-${item.id}`,
          type: "command",
          target: "refresh",
          paramKey: item.id,
          payload: { newQuestion: editedQuestion.trim() }
        });
      }
    } else if (e.key === 'Escape') {
      setIsEditingQuestion(false);
      setEditedQuestion(question);
    }
  };

  const handleQuestionBlur = () => {
    // Don't reset for new cards
    if ((item as any).isEditing) return;
    setIsEditingQuestion(false);
    setEditedQuestion(question);
  };

  const handleNewCardSubmit = () => {
    console.log('[ChatMessageCard] handleNewCardSubmit called, question:', editedQuestion, 'onAction:', !!onAction);
    if (editedQuestion.trim() && onAction) {
      console.log('[ChatMessageCard] Calling onAction with submitNewCard');
      onAction({
        id: `submit-${item.id}`,
        type: 'command',
        target: 'submitNewCard',
        paramKey: item.id,
        payload: { question: editedQuestion.trim() }
      });
    } else {
      console.log('[ChatMessageCard] Cannot submit - question empty or no onAction');
    }
  };

  const handleNewCardCancel = () => {
    if (onAction) {
      onAction({
        id: `cancel-${item.id}`,
        type: 'command',
        target: 'cancelNewCard',
        paramKey: item.id
      });
    }
  };

  const handleNewCardKeyDown = (e: React.KeyboardEvent) => {
    console.log('[ChatMessageCard] Key pressed:', e.key, 'shiftKey:', e.shiftKey, 'question:', editedQuestion);
    if (e.key === 'Enter' && !e.shiftKey && editedQuestion.trim()) {
      e.preventDefault();
      console.log('[ChatMessageCard] Submitting new card');
      handleNewCardSubmit();
    } else if (e.key === 'Escape') {
      console.log('[ChatMessageCard] Cancelling new card');
      handleNewCardCancel();
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setEditedQuestion(suggestion);
    setQuestionSuggestions([]);
    if (questionInputRef.current) {
      questionInputRef.current.focus();
    }
  };

  // Zeige Ladeanimation wenn answer leer ist (aber nicht bei neuen Karten)
  const isNewCard = (item as any).isEditing;
  const isLoading = !answer && !isNewCard;

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCardMenu(!showCardMenu);
  };

  const handleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCardMenu(false);

    // Notify parent component to toggle fullscreen
    if (onAction) {
      onAction({
        id: `toggleFullscreen-${item.id}`,
        type: "command",
        target: "toggleFullscreen",
        paramKey: item.id
      });
    }
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsBookmarked(!isBookmarked);
    setShowCardMenu(false);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCardMenu(false);
    if (onAction) {
      onAction({
        id: `remove-${item.id}`,
        type: "command",
        target: "removeCard",
        paramKey: item.id
      });
    }
  };

  const renderQuestionHeader = () => {
    const isNewCard = (item as any).isEditing;

    // Don't render header in fullscreen mode
    if (isFullscreen) {
      return null;
    }

    return (
      <>
        {isEditingQuestion || isNewCard ? (
          <div className="chat-new-card-input-container">
            <textarea
              ref={questionInputRef}
              className="text-question-new-edit"
              value={editedQuestion}
              onChange={(e) => setEditedQuestion(e.target.value)}
              onKeyDown={isNewCard ? handleNewCardKeyDown : handleQuestionKeyDown}
              onBlur={isNewCard ? undefined : handleQuestionBlur}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              rows={1}
              placeholder={isNewCard ? "Frage eingeben..." : ""}
              style={{ height: 'auto', minHeight: '1.4em' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = target.scrollHeight + 'px';
              }}
              onFocus={(e) => {
                const target = e.target as HTMLTextAreaElement;
                const length = target.value.length;
                target.setSelectionRange(length, length);
              }}
            />
          </div>
        ) : (
          <EditableTitle
            title={question}
            className="text-question"
            onTitleChange={(newQuestion) => {
              if (onAction && newQuestion !== question) {
                onAction({
                  id: `refresh-${item.id}`,
                  type: "command",
                  target: "refresh",
                  paramKey: item.id,
                  payload: { newQuestion: newQuestion }
                });
              }
            }}
          />
        )}
        {!isNewCard && (
          <div className="chat-card-menu-container" ref={menuRef}>
            <button
              className="chat-card-menu-button"
              onClick={handleMenuToggle}
              title="Optionen"
            >
              ⋮
            </button>
            {showCardMenu && (
              <div className="chat-card-menu-popup">
                <button className="chat-card-menu-item" onClick={handleFullscreen} title="Vollbild">
                  <span className="chat-card-menu-icon">{isFullscreen ? '⊟' : '⊞'}</span>
                  <span className="chat-card-menu-label">{isFullscreen ? 'Normal' : 'Vollbild'}</span>
                </button>
                <button className="chat-card-menu-item" onClick={handleBookmark} title="Markieren">
                  <span className="chat-card-menu-icon">{isBookmarked ? '★' : '☆'}</span>
                  <span className="chat-card-menu-label">{isBookmarked ? 'Markiert' : 'Markieren'}</span>
                </button>
                <button className="chat-card-menu-item chat-card-menu-item-danger" onClick={handleClose} title="Schließen">
                  <span className="chat-card-menu-icon">✕</span>
                  <span className="chat-card-menu-label">Schließen</span>
                </button>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  // New card - show header with input and suggestions in body
  if (isNewCard) {
    return (
      <div className={isFullscreen ? 'card-fullscreen' : ''}>
        {renderQuestionHeader()}
        {questionSuggestions.length > 0 && (
          <div className="chat-suggestions-container">
            <div className="chat-suggestions-header">Vorschläge aus der Historie:</div>
            <div className="chat-suggestions-list">
              {questionSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="chat-suggestion-item"
                  onClick={() => handleSelectSuggestion(suggestion)}
                  title="Klicken zum Übernehmen"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={isFullscreen ? 'card-fullscreen' : ''}>
        {renderQuestionHeader()}
        <div className="text-loading">
          <div className="chat-loading-dots">
            <div className="chat-loading-dot"></div>
            <div className="chat-loading-dot"></div>
            <div className="chat-loading-dot"></div>
          </div>
          <span className="chat-loading-text">KI generiert Antwort...</span>
        </div>
      </div>
    );
  }

  // Summary Mode (KPI View) - entfernt, um immer die Tabelle anzuzeigen
  // if (displayMode === 'summary' && kpiData) {
  //   return (
  //     <div className="text-card clickable" onClick={handleClick}>
  //       <div className="text-header">
  //         <div className="text-question">{question}</div>
  //       </div>
  //       <div className="text-body">
  //         <div className="text-answer">{answer}</div>
  //         <div className="text-kpi">
  //           <div className="kpi-value">{kpiData.value}</div>
  //           <div className="kpi-label">{kpiData.label}</div>
  //           <div className="kpi-hint">Klicken für Details →</div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // Tiles Mode (Launchpad Tiles)
  const msgPayload = config?.messagePayload as any;
  if (msgPayload?.format === 'tiles' && msgPayload?.tiles && Array.isArray(msgPayload.tiles)) {
    return (
      <div>
        {renderQuestionHeader()}
        <div className="launchpad-tiles-grid" style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${msgPayload.columns || 3}, 1fr)`,
          gap: '1rem',
          padding: '1rem'
        }}>
          {msgPayload.tiles.map((tile: any) => (
            <div
              key={tile.id}
              className="launchpad-tile"
              style={{
                padding: '1.5rem',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textAlign: 'center'
              }}
              onClick={() => {
                if (onAction && tile.action) {
                  onAction({
                    id: tile.action.id,
                    type: tile.action.type,
                    target: tile.action.target
                  });
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{tile.icon || '📦'}</div>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{tile.label}</div>
              {tile.description && (
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{tile.description}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Object List Mode (Generic Tile View)
  if (msgPayload?.format === 'object-list' && msgPayload?.results && Array.isArray(msgPayload.results)) {
    const objectListItem: ObjectListFeedItem = {
      id: item.id,
      type: 'object-list',
      title: question || 'Objektliste',
      config: {
        items: msgPayload.results,
        total: msgPayload.total || msgPayload.resultsCount || msgPayload.results.length,
        objectType: msgPayload.objectType,
        displayFields: msgPayload.displayFields,
        titleField: msgPayload.titleField,
        subtitleField: msgPayload.subtitleField,
        iconField: msgPayload.iconField
      }
    };

    return (
      <div>
        {renderQuestionHeader()}
        <ObjectListCard
          item={objectListItem}
          data={null}
          loading={false}
          onAction={onAction}
          onReload={() => { }}
          chatHistory={chatHistory}
        />
      </div>
    );
  }

  // Object Detail Mode (Single Object View)
  if (msgPayload?.format === 'object-detail' && msgPayload?.object) {
    const objectDetailItem: ObjectDetailFeedItem = {
      id: item.id,
      type: 'object-detail',
      title: question || 'Objekt-Details',
      config: {
        object: msgPayload.object,
        objectType: msgPayload.objectType,
        objectId: msgPayload.objectId,
        recommendedActions: msgPayload.recommendedActions
      }
    };

    return (
      <div>
        {renderQuestionHeader()}
        <ObjectDetailCard
          item={objectDetailItem}
          data={null}
          loading={false}
          onAction={onAction}
          onReload={() => { }}
          chatHistory={chatHistory}
        />
      </div>
    );
  }

  // Workflow UI Mode
  if (workflowUI) {
    const ui = workflowUI as WorkflowUIConfig;

    return (
      <div>
        {renderQuestionHeader()}
        <div className="text-answer" dangerouslySetInnerHTML={{ __html: answer }} />

        {/* Download Button for Certificate ZIP */}
        {(() => {
          const hasDownloadUrl = !!(config?.messagePayload as any)?.metadata?.downloadUrl;
          console.log('[ChatMessageCard Workflow] Download button check:', {
            hasConfig: !!config,
            hasMessagePayload: !!config?.messagePayload,
            messagePayload: config?.messagePayload,
            metadata: (config?.messagePayload as any)?.metadata,
            downloadUrl: (config?.messagePayload as any)?.metadata?.downloadUrl,
            hasDownloadUrl
          });
          return hasDownloadUrl;
        })() && (
            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const downloadUrl = (config?.messagePayload as any)?.metadata?.downloadUrl;
                  console.log('[ChatMessageCard] Download button clicked, URL:', downloadUrl);
                  if (downloadUrl) {
                    window.location.href = downloadUrl;
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                🔐 Öffentliches Zertifikat herunterladen
              </button>
            </div>
          )}

        <div className="text-workflow" onClick={(e) => e.stopPropagation()}>
          <WorkflowRenderer
            ui={ui}
            stepId={ui.steps?.[0]?.id || "step-1"}
            language="de-DE"
            fallbackLanguage="en-US"
            context={workflowContext}
            onBindingChange={(tile, binding, value) => {
              if (binding) {
                setWorkflowContext(prev => ({
                  ...prev,
                  session: {
                    ...prev.session,
                    [binding]: value
                  }
                }));
              }
            }}
            onAction={async (action) => {
              console.log("Workflow Action:", action, "Context:", workflowContext);

              if (action.target && String(action.target) === 'submitForm') {
                try {
                  // Extract form data from context
                  const formData = { ...workflowContext.session };

                  // Get businessObjectType and operation from payload
                  const msgPayload = (item as any).config?.messagePayload;
                  const businessObjectType = msgPayload?.businessObjectType;
                  const operation = msgPayload?.operation;
                  const recordId = msgPayload?.recordId;

                  console.log('Submitting form:', { businessObjectType, operation, formData, recordId });

                  // Prepare payload - remove empty/undefined fields and fix formats
                  const payload: Record<string, unknown> = {};
                  for (const [key, value] of Object.entries(formData)) {
                    // Skip empty strings, undefined, and null
                    if (value === '' || value === undefined || value === null) {
                      continue;
                    }

                    // Convert datetime-local to ISO 8601 UTC format
                    if (key.includes('At') && typeof value === 'string' && value.includes('T')) {
                      // Input format: "2025-11-26T10:51" (datetime-local)
                      // Required: "2025-11-26T10:51:00.000Z" (ISO 8601 UTC)
                      const date = new Date(value);
                      payload[key] = date.toISOString();
                    } else {
                      payload[key] = value;
                    }
                  }

                  // Validate and remove problematic optional fields
                  if (payload.apiKey && typeof payload.apiKey === 'string' && payload.apiKey.length < 32) {
                    delete payload.apiKey; // Remove if too short, will use backend default
                  }
                  if (payload.metadata === '' || (typeof payload.metadata === 'object' && Object.keys(payload.metadata as object).length === 0)) {
                    delete payload.metadata;
                  }

                  console.log('Cleaned payload:', payload);

                  // Map businessObjectType to API endpoint
                  const endpointMap: Record<string, string> = {
                    hardwareConnector: '/api/hardware/connectors',
                    hardware: '/api/hardware/devices',
                    // Add more mappings as needed
                  };

                  const endpoint = endpointMap[businessObjectType];
                  if (!endpoint) {
                    throw new Error(`No API endpoint configured for ${businessObjectType}`);
                  }

                  // Determine HTTP method and URL
                  const isCreate = operation === 'create';
                  const url = isCreate ? endpoint : `${endpoint}/${recordId}`;
                  const method = isCreate ? 'POST' : 'PUT';

                  // Send request
                  const response = await fetch(url, {
                    method,
                    headers: {
                      ...getAuthHeaders(),
                      'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(payload),
                  });

                  if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`API error: ${response.status} - ${errorText}`);
                  }

                  const result = await response.json();
                  console.log('Form submitted successfully:', result);

                  // Check if API-Key was generated (terminal creation)
                  if (result.apiKey && typeof result.apiKey === 'string') {
                    const copyToClipboard = () => {
                      navigator.clipboard.writeText(result.apiKey);
                    };

                    const message = `Terminal erfolgreich erstellt!\n\n🔑 API-Key: ${result.apiKey}\n\n⚠️ WICHTIG: Dieser API-Key wird nur einmalig angezeigt und kann nicht erneut abgerufen werden. Bitte speichern Sie ihn sicher.`;

                    // Copy to clipboard automatically
                    copyToClipboard();

                    alert(message + '\n\n✅ Der API-Key wurde in die Zwischenablage kopiert.');
                  } else {
                    // Show generic success message
                    alert(`${businessObjectType} erfolgreich ${isCreate ? 'erstellt' : 'gespeichert'}!`);
                  }

                  // Optional: Trigger page refresh or update
                  // window.location.reload();

                } catch (error) {
                  console.error('Form submission error:', error);
                  alert(`Fehler beim Speichern: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
                }
              }
            }}
          />
        </div>
      </div>
    );
  }

  if (!tableData || tableData.rows.length === 0) {
    // Check if camera upload is required
    const msgPayload = config?.messagePayload as any;
    const requiresCameraUpload = msgPayload?.requiresCameraUpload === true;

    console.log('[ChatMessageCard] Checking camera upload:', {
      requiresCameraUpload,
      msgPayload,
      hasOnAction: !!onAction
    });

    if (requiresCameraUpload) {
      console.log('[ChatMessageCard] Rendering camera upload UI');
      // Camera upload UI
      return (
        <div>
          {renderQuestionHeader()}
          <div className="text-answer" dangerouslySetInnerHTML={{ __html: answer }} />

          {/* Download Button for Certificate ZIP */}
          {(() => {
            const hasDownloadUrl = !!(config?.messagePayload as any)?.metadata?.downloadUrl;
            console.log('[ChatMessageCard Camera] Download button check:', {
              hasConfig: !!config,
              hasMessagePayload: !!config?.messagePayload,
              messagePayload: config?.messagePayload,
              metadata: (config?.messagePayload as any)?.metadata,
              downloadUrl: (config?.messagePayload as any)?.metadata?.downloadUrl,
              hasDownloadUrl
            });
            return hasDownloadUrl;
          })() && (
              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const downloadUrl = (config?.messagePayload as any)?.metadata?.downloadUrl;
                    console.log('[ChatMessageCard] Download button clicked, URL:', downloadUrl);
                    if (downloadUrl) {
                      window.location.href = downloadUrl;
                    }
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.4)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  🔐 Öffentliches Zertifikat herunterladen
                </button>
              </div>
            )}

          <CameraUpload
            onImageCaptured={async (imageData: string) => {
              console.log('[ChatMessageCard] Image captured, sending to backend');
              // Send image to backend directly from here
              const response = await fetch('/api/chat/camera-image', {
                method: 'POST',
                headers: {
                  ...getAuthHeaders(),
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  imageData,
                  query: msgPayload?.query || question || 'was siehst du',
                  sessionId: (item as any).sessionId
                })
              });

              if (!response.ok) {
                throw new Error('Upload failed');
              }

              const result = await response.json();

              // Return result to CameraUpload component
              return {
                analysis: result.analysis,
                imageUrl: result.imageUrl || (result.imageUrls && result.imageUrls[0]) || ''
              };
            }}
          />
        </div>
      );
    }

    // Extract recommended actions from messagePayload
    const recommendedActions = Array.isArray((config?.messagePayload as any)?.recommendedActions)
      ? (config?.messagePayload as any)?.recommendedActions as Array<{ label: string; command: string; description?: string }>
      : [];

    // No drill-down for non-table content
    return (
      <div>
        {renderQuestionHeader()}
        <div className="text-answer" dangerouslySetInnerHTML={{ __html: answer }} />

        {/* Download Button for Certificate ZIP */}
        {(() => {
          const hasDownloadUrl = !!(config?.messagePayload as any)?.metadata?.downloadUrl;
          console.log('[ChatMessageCard Standard] Download button check:', {
            hasConfig: !!config,
            hasMessagePayload: !!config?.messagePayload,
            messagePayload: config?.messagePayload,
            metadata: (config?.messagePayload as any)?.metadata,
            downloadUrl: (config?.messagePayload as any)?.metadata?.downloadUrl,
            hasDownloadUrl
          });
          return hasDownloadUrl;
        })() && (
            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const downloadUrl = (config?.messagePayload as any)?.metadata?.downloadUrl;
                  console.log('[ChatMessageCard] Download button clicked, URL:', downloadUrl);
                  if (downloadUrl) {
                    // Trigger download
                    window.location.href = downloadUrl;
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                🔐 Öffentliches Zertifikat herunterladen
              </button>
            </div>
          )}

        {/* Recommended Actions Buttons */}
        {recommendedActions.length > 0 && (
          <div style={{
            marginTop: '1.5rem',
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap',
            paddingTop: '1rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            {recommendedActions.map((action, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  // Check if action has downloadUrl (direct download link)
                  if ((action as any).downloadUrl) {
                    console.log('[ChatMessageCard] Download action clicked:', (action as any).downloadUrl);
                    window.location.href = (action as any).downloadUrl;
                    return;
                  }
                  // Otherwise, send as command
                  console.log('[ChatMessageCard] Recommended action clicked:', action.command, 'onAction available:', !!onAction);
                  if (onAction) {
                    // Create FeedAction object for the command
                    const feedAction = {
                      id: `action-${idx}`,
                      type: 'send-message' as const,
                      label: action.label,
                      payload: {
                        message: action.command
                      }
                    };
                    onAction(feedAction);
                  } else {
                    console.error('[ChatMessageCard] onAction is not defined!');
                  }
                }}
                title={action.description}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 1.25rem',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#2563eb';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#3b82f6';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const handleSort = (field: string) => {
    setSortConfig(prev => {
      if (prev?.field === field) {
        // Toggle direction
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // New field, default to ascending
      return { field, direction: 'asc' };
    });
  };

  // Sort data if sortConfig is set
  const sortedRows = useMemo(() => {
    if (!sortConfig) return tableData.rows;

    const sorted = [...tableData.rows].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Fallback: convert to string and compare
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortConfig.direction === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return sorted;
  }, [tableData.rows, sortConfig]);

  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentRows = sortedRows.slice(startIndex, endIndex);

  // If creating new or a row is selected, show detail view instead of table
  if (isCreatingNew || selectedRowIndex !== null) {
    const currentRowData = isCreatingNew ? editedRowData : { ...tableData.rows[selectedRowIndex!], ...editedRowData };

    const handleFieldClick = (fieldName: string) => {
      setIsEditingField(fieldName);
    };

    const handleFieldChange = (fieldName: string, value: any) => {
      setEditedRowData(prev => ({
        ...prev,
        [fieldName]: value
      }));
    };

    const handleFieldBlur = () => {
      setIsEditingField(null);
    };

    const handleSave = async () => {
      if (Object.keys(editedRowData).length === 0) return;

      setIsSaving(true);
      try {
        if (isCreatingNew) {
          // Create new record
          if (onAction) {
            onAction({
              id: `create-${item.id}`,
              type: "command",
              target: "createRecord",
              paramKey: item.id,
              payload: {
                data: editedRowData
              }
            });
          }

          // Add to local table data
          tableData.rows.push(editedRowData);
          setIsCreatingNew(false);
          setEditedRowData({});
          alert('Neuer Eintrag erstellt');
        } else {
          // Update existing record
          if (onAction) {
            onAction({
              id: `save-${item.id}`,
              type: "command",
              target: "saveRecord",
              paramKey: item.id,
              payload: {
                rowIndex: selectedRowIndex,
                updates: editedRowData,
                originalData: tableData.rows[selectedRowIndex!]
              }
            });
          }

          // Update the local table data
          tableData.rows[selectedRowIndex!] = { ...tableData.rows[selectedRowIndex!], ...editedRowData };
          setEditedRowData({});
          alert('Änderungen gespeichert');
        }
      } catch (error) {
        console.error('Save error:', error);
        alert('Fehler beim Speichern');
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div>
        {renderQuestionHeader()}
        <div className="chat-table-detail-fields">
          {tableData.columns.map((col) => {
            const value = currentRowData[col.field];
            const isEditing = isEditingField === col.field;

            // In create mode, always show as editable/empty if no value
            const shouldAutoEdit = isCreatingNew && !value;
            const displayValue = value ? formatTableValue(value, {
              format: col.format,
              valueMap: col.valueMap,
              locale: userLocale
            }) : '';

            return (
              <div key={col.id} className="chat-table-detail-field-card">
                <div className="chat-table-detail-field-label">{col.label}</div>
                {(isEditing || shouldAutoEdit) ? (
                  <input
                    type="text"
                    className="chat-table-detail-field-input"
                    value={value ?? ''}
                    onChange={(e) => handleFieldChange(col.field, e.target.value)}
                    onBlur={handleFieldBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleFieldBlur();
                      } else if (e.key === 'Escape') {
                        setEditedRowData(prev => {
                          const newData = { ...prev };
                          delete newData[col.field];
                          return newData;
                        });
                        handleFieldBlur();
                      }
                    }}
                    autoFocus={isEditing}
                    placeholder={isCreatingNew ? `${col.label} eingeben` : ''}
                  />
                ) : (
                  <div
                    className="chat-table-detail-field-value chat-table-detail-field-editable"
                    onClick={() => handleFieldClick(col.field)}
                    title="Klicken zum Bearbeiten"
                  >
                    {displayValue || <span style={{ color: '#9ca3af' }}>Leer</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="chat-table-detail-navigation">
          <div className="chat-table-detail-navigation-left">
            <button
              className="chat-table-detail-close-button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRowIndex(null);
                setIsCreatingNew(false);
                setEditedRowData({});
                setIsEditingField(null);
              }}
              title="Zur Tabelle zurück"
            >
              ☰
            </button>
            {Object.keys(editedRowData).length > 0 && (
              <button
                className="chat-table-detail-save-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                disabled={isSaving}
                title={isCreatingNew ? "Neuen Eintrag speichern" : "Änderungen speichern"}
              >
                {isSaving ? '...' : (isCreatingNew ? '💾 Erstellen' : '💾 Speichern')}
              </button>
            )}
          </div>
          {!isCreatingNew && (
            <div className="chat-table-detail-navigation-right">
              <button
                className="chat-table-detail-nav-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRowIndex(prev => prev !== null && prev > 0 ? prev - 1 : prev);
                  setEditedRowData({});
                  setIsEditingField(null);
                }}
                disabled={selectedRowIndex === 0}
                title="Vorheriges Element"
              >
                ←
              </button>
              <span className="chat-table-detail-nav-info">
                {selectedRowIndex! + 1} / {tableData.rows.length}
              </span>
              <button
                className="chat-table-detail-nav-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRowIndex(prev => prev !== null && prev < tableData.rows.length - 1 ? prev + 1 : prev);
                  setEditedRowData({});
                  setIsEditingField(null);
                }}
                disabled={selectedRowIndex === tableData.rows.length - 1}
                title="Nächstes Element"
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={isFullscreen ? 'card-fullscreen' : ''}>
      {renderQuestionHeader()}
      <div className="text-table" onClick={(e) => e.stopPropagation()}>
        <div className="chat-card-table-container">{ }
          <table className="chat-card-table">
            <thead>
              <tr>
                {tableData.columns.map((col) => {
                  const isSorted = sortConfig?.field === col.field;
                  const sortIcon = isSorted ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

                  return (
                    <th
                      key={col.id}
                      className={`${col.align === "right" ? "is-right" : col.align === "center" ? "is-center" : ""} sortable ${isSorted ? 'is-sorted' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSort(col.field);
                      }}
                      title="Klicken zum Sortieren"
                    >
                      <div className="table-cell-content">{col.label}{sortIcon}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((row, idx) => {
                const absoluteIndex = startIndex + idx;
                return (
                  <tr
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRowIndex(absoluteIndex);
                    }}
                    style={{ cursor: 'pointer' }}
                    className={selectedRowIndex === absoluteIndex ? 'is-selected' : ''}
                  >
                    {tableData.columns.map((col) => {
                      const value = row[col.field];
                      const displayValue = formatTableValue(value, {
                        format: col.format,
                        valueMap: col.valueMap,
                        locale: userLocale
                      });

                      return (
                        <td
                          key={col.id}
                          className={col.align === "right" ? "is-right" : col.align === "center" ? "is-center" : ""}
                          title={displayValue}
                        >
                          <div className="table-cell-content">{displayValue}</div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="chat-card-table-pagination">
          <div className="chat-card-table-total-count">
            <button
              className="chat-table-detail-view-button"
              onClick={(e) => {
                e.stopPropagation();
                if (sortedRows.length > 0) {
                  setSelectedRowIndex(0);
                }
              }}
              disabled={sortedRows.length === 0}
              title="Zur Detailansicht wechseln"
            >
              ⋮
            </button>
            {tableData.rows.length} Einträge
            <button
              className="chat-table-create-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsCreatingNew(true);
                setEditedRowData({});
                setIsEditingField(null);
              }}
              title="Neuen Eintrag erstellen"
            >
              +
            </button>
          </div>
          {totalPages > 1 && (
            <>
              <div className="chat-card-table-page-size">
                <button
                  className="chat-card-table-page-size-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const options = tableData.pagination?.pageSizeOptions || [10, 25, 50, 100];
                    const currentIndex = options.indexOf(pageSize);
                    if (currentIndex > 0) {
                      setPageSize(options[currentIndex - 1]);
                      setCurrentPage(1);
                    }
                  }}
                  disabled={pageSize <= (tableData.pagination?.pageSizeOptions?.[0] || 10)}
                  title="Weniger Zeilen"
                >
                  −
                </button>
                <span className="chat-card-table-page-size-value">{pageSize} Zeilen</span>
                <button
                  className="chat-card-table-page-size-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const options = tableData.pagination?.pageSizeOptions || [10, 25, 50, 100];
                    const currentIndex = options.indexOf(pageSize);
                    if (currentIndex < options.length - 1) {
                      setPageSize(options[currentIndex + 1]);
                      setCurrentPage(1);
                    }
                  }}
                  disabled={pageSize >= (tableData.pagination?.pageSizeOptions?.[tableData.pagination.pageSizeOptions.length - 1] || 100)}
                  title="Mehr Zeilen"
                >
                  +
                </button>
              </div>
              <div className="chat-card-table-pagination-controls">
                <button
                  className="chat-card-table-pagination-button"
                  onClick={(e) => { e.stopPropagation(); setCurrentPage((p) => Math.max(1, p - 1)); }}
                  disabled={currentPage === 1}
                  title="Vorherige Seite"
                >
                  ←
                </button>
                <span>
                  Seite {currentPage} von {totalPages}
                </span>
                <button
                  className="chat-card-table-pagination-button"
                  onClick={(e) => { e.stopPropagation(); setCurrentPage((p) => Math.min(totalPages, p + 1)); }}
                  disabled={currentPage === totalPages}
                  title="Nächste Seite"
                >
                  →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
